import type { Variation } from './types';

/** Minimal structural interface for an ID-based flag config. */
interface IdBasedWeightedVariation {
  variationId: string;
  weight: number;
}

interface IdBasedRollout {
  variations: IdBasedWeightedVariation[];
}

export interface IdBasedFlagConfig {
  on: boolean;
  offVariationId?: string;
  targets?: Array<{ variationId: string }>;
  contextTargets?: Array<{ variationId: string }>;
  rules?: Array<{
    variationId?: string;
    rollout?: IdBasedRollout;
  }>;
  fallthrough?: {
    variationId?: string;
    rollout?: IdBasedRollout;
  };
}

/**
 * Returns the active variations for an ID-based flag config.
 *
 * This is the canonical implementation. Callers that work with the index-based
 * {@link FeatureFlagConfig} (the OpenAPI shape) should use
 * {@link getFlagActiveVariationsFromConfig} instead — it converts indices to
 * IDs internally and delegates here.
 */
export function getFlagActiveVariationsFromIdBasedConfig(
  config: IdBasedFlagConfig,
  variations: readonly Variation[],
): Array<[variationIndex: number, variation: Variation]> {
  const resolve = (id: string): number => variations.findIndex((v) => v._id === id);

  const activeIds = new Set<string>();

  if (!config.on) {
    if (config.offVariationId) {
      activeIds.add(config.offVariationId);
    }
  } else {
    // This logic also lives on the backend in internal/reps2/flag_rep2.go#MakeConfigurationRep.
    // If you add or remove variation types here, you may also need to update on the backend.
    config.rules?.forEach((r) => {
      r.rollout?.variations.forEach((wv) => {
        if (wv.weight !== 0) {
          activeIds.add(wv.variationId);
        }
      });
      if (r.variationId) {
        activeIds.add(r.variationId);
      }
    });

    config.contextTargets?.forEach((t) => activeIds.add(t.variationId));
    config.targets?.forEach((t) => activeIds.add(t.variationId));

    config.fallthrough?.rollout?.variations.forEach((wv) => {
      if (wv.weight > 0) {
        activeIds.add(wv.variationId);
      }
    });

    if (config.fallthrough?.variationId) {
      activeIds.add(config.fallthrough.variationId);
    }
  }

  const result: Array<[number, Variation]> = [];
  for (const id of activeIds) {
    const index = resolve(id);
    if (index !== -1) {
      const variation = variations[index];
      if (variation) {
        result.push([index, variation]);
      }
    }
  }
  return result;
}
